import { ArrowLeft, Globe } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { authApi } from "@/api/endpoints/auth";
import { Button, TextField } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";

import { useStokkieTitle } from "../stokkie/useStokkieTitle";
import { errorText } from "./errors";
import { StokkieLogo } from "./StokkieLogo";

type Step = "phone" | "code" | "details";

/**
 * "Register as a Stokvel" — and signing in again afterwards.
 *
 * One screen for both, because from the member's side they are the same act:
 * type your number, type the PIN from the SMS. Only a number the platform has
 * never seen takes the third step, a name and the Terms of Use. A shop owner
 * does not come through here; they sign in to the back office as always and
 * open their stokvels from there, with the same account.
 *
 * `?next=` carries an invite link through sign-up, so a member who tapped the
 * chairperson's link on WhatsApp lands in the group, not on a home screen.
 */
export function PhoneSignInPage() {
  useStokkieTitle();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const signInWith = useAuthStore((s) => s.signInWith);

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "authenticated" && step !== "details")
    return <Navigate to={next} replace />;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  const sendCode = (event?: FormEvent) => {
    event?.preventDefault();
    void run(async () => {
      await authApi.sendPhoneCode(phone);
      setCode("");
      setStep("code");
    });
  };

  const verify = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await authApi.verifyPhoneCode(phone, code);
      if ("session" in result) {
        await signInWith(result.session);
        void navigate(next, { replace: true });
      } else {
        setToken(result.registrationToken);
        setStep("details");
      }
    });
  };

  const register = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const session = await authApi.registerWithPhone({
        registrationToken: token,
        firstName,
        lastName,
        email,
        acceptedTerms: accepted,
      });
      await signInWith(session);
      void navigate(next, { replace: true });
    });
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-canvas px-4 py-6 sm:justify-center sm:py-10">
      <Link
        to="/stokkie"
        className="mb-4 inline-flex h-11 items-center gap-2 self-start rounded-full bg-surface px-4 font-semibold text-brand-600 shadow-card sm:self-center"
      >
        <Globe className="size-5" aria-hidden />
        Visit the Stokkie Stokvel website
      </Link>
      <div className="w-full max-w-sm rounded-[1.375rem] bg-surface p-6 shadow-pop">
        <Link
          to="/stokkie"
          aria-label="Stokkie Stokvel website"
          className="flex justify-center"
        >
          <StokkieLogo className="w-full max-w-[18rem]" />
        </Link>
        <h1 className="mt-5 text-center text-xl font-bold text-ink-900">
          Sign in or join
        </h1>
        <p className="mt-1 text-center text-sm text-ink-500">
          {step === "phone" &&
            "Start or join a stokvel with your cellphone number."}
          {step === "code" && `We sent a 6-digit PIN by SMS to ${phone}.`}
          {step === "details" &&
            "Your number is confirmed. Tell us your name to finish."}
        </p>

        {step === "phone" && (
          <form onSubmit={sendCode} className="mt-6 space-y-4">
            <TextField
              label="Cellphone number"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="082 123 4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={busy}
            >
              Send me a PIN
            </Button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <TextField
              label="PIN from the SMS"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={busy}
            >
              Continue
            </Button>
            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-ink-500 hover:text-ink-900"
                onClick={() => setStep("phone")}
              >
                <ArrowLeft className="size-4" aria-hidden />
                Change number
              </button>
              <button
                type="button"
                className="font-medium text-brand-500 hover:underline disabled:opacity-50"
                onClick={() => sendCode()}
                disabled={busy}
              >
                Send again
              </button>
            </div>
          </form>
        )}

        {step === "details" && (
          <form onSubmit={register} className="mt-6 space-y-4">
            <TextField
              label="First name"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              label="Surname"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <TextField
              label="Email (optional)"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="flex items-start gap-3 text-sm text-ink-600">
              <input
                type="checkbox"
                className="mt-0.5 size-5 shrink-0 accent-brand-500"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                required
              />
              <span>
                I accept the Stokkie Stokvel Terms of Use and Privacy Notice. My
                number is only shown to other members if I choose to share it.
              </span>
            </label>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={busy}
              disabled={!accepted}
            >
              Create my account
            </Button>
          </form>
        )}

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-control bg-danger-50 px-3 py-2 text-sm text-danger-700"
          >
            {error}
          </p>
        )}

        <p className="mt-6 text-center text-sm text-ink-500">
          Run a spaza shop?{" "}
          <Link
            to="/login?mode=password"
            className="font-medium text-brand-500 hover:underline"
          >
            Sign in to your shop
          </Link>
        </p>
      </div>
    </div>
  );
}

/** Only a path inside the stokvel portal — never somewhere a link chose for us. */
function safeNext(raw: string | null): string {
  return raw && raw.startsWith("/stokvel") && !raw.startsWith("//")
    ? raw
    : "/stokvel";
}
